import { useEffect, useState } from "react";
import { Truck, BadgeDollarSign, Sparkles, ShieldCheck, Gift, Percent } from "lucide-react";
import API from "@/lib/api";

// Icon lookup — admin picks a slug from this list in the Banners tab.
const ICONS = {
  truck: Truck,
  "badge-dollar-sign": BadgeDollarSign,
  sparkles: Sparkles,
  "shield-check": ShieldCheck,
  gift: Gift,
  percent: Percent,
};

/**
 * Below-hero rotating promo strip. Fetches all admin-managed banners with
 * `type === "promo-strip"` and cycles through the active ones every 5s with
 * a fade transition, so the slot is never blank while at least one strip
 * is enabled. Hides itself entirely if there are none active.
 */
export default function FreeShippingTruckBanner() {
  const [strips, setStrips] = useState([]);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await API.get("/banners");
        if (!alive) return;
        const list = Array.isArray(data)
          ? data.filter((b) => b.type === "promo-strip")
          : [];
        setStrips(list);
      } catch { /* silent — banner slot just stays hidden */ }
    })();
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (strips.length < 2) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % strips.length), 5000);
    return () => clearInterval(t);
  }, [strips.length]);

  if (strips.length === 0) return null;
  const active = strips[idx % strips.length];
  const Icon = ICONS[active.icon] || Truck;
  const bg = active.bg_color || "#059669";
  const fg = active.text_color || "#ffffff";

  return (
    <div
      className="relative w-full overflow-hidden border-y-2 border-emerald-400/50 shadow-inner"
      style={{ height: 68, background: bg, color: fg }}
      data-testid="free-shipping-truck-banner"
    >
      <style>{`
        @keyframes glpflo-strip-fade {
          0%   { opacity: 0; transform: translateY(4px); }
          15%  { opacity: 1; transform: translateY(0); }
          85%  { opacity: 1; transform: translateY(0); }
          100% { opacity: 0; transform: translateY(-4px); }
        }
        @keyframes glpflo-strip-bounce {
          0%,100% { transform: translateY(0); }
          25%     { transform: translateY(-1.5px); }
          75%     { transform: translateY(1.5px); }
        }
      `}</style>
      <div
        key={active.id}
        className="absolute inset-0 flex items-center justify-center gap-3 px-4 whitespace-nowrap"
        style={{ animation: strips.length > 1 ? "glpflo-strip-fade 5s ease-in-out both" : undefined }}
        data-testid={`promo-strip-${active.id}`}
      >
        <div
          className="flex items-center justify-center w-11 h-11 rounded-lg bg-white text-emerald-700 shadow-lg shrink-0"
          style={{ animation: "glpflo-strip-bounce 0.4s ease-in-out infinite" }}
        >
          <Icon className="w-6 h-6" />
        </div>
        <div className="bg-white text-emerald-800 font-extrabold uppercase tracking-widest text-sm sm:text-base px-4 py-1 rounded-full shadow-lg ring-2 ring-yellow-300 leading-tight shrink-0">
          {active.text}
        </div>
        {active.subtitle && (
          <span className="text-white/90 text-xs font-medium leading-tight shrink-0 hidden sm:inline">
            {active.subtitle}
          </span>
        )}
      </div>
    </div>
  );
}
